import { getStorageItem, removeStorageItem, setStorageItem } from 'utils/ui/storage';
import logger from 'utils/log';

/**
 * AVS-4461: the marker of a PENDING master claim. A standby whose connection was built without
 * its own media claims a released slot at once, but starts camera and mic only when the dealer
 * clicks "Take over". The marker records which claim that was, so the pending state survives a
 * reload: `isVoipClaimPendingMedia` holds only while the live slot still matches it (same
 * session, this tab, same gen), so a moved slot makes the marker inert.
 *
 * Kept in `sessionStorage` (it survives a reload but stays per tab; a duplicated tab copies it
 * and stays pending, the safe side: no camera starts without a click) and mirrored in memory,
 * so reads cost no parse and a storage failure still leaves this page consistent.
 *
 * Removed only by a successful takeover, a release, the end of the session, or entering a DIFFERENT
 * session — never by `clearVoipClaim()`, which also runs on every fresh page load and cannot tell a
 * reload apart.
 */
export interface IVoipClaimPending {
  sessionId: string;
  gen: number;
}

const STORAGE_KEY = 'voip-claim-pending';

// `undefined` until first read: the mirror loads lazily, so a fresh page picks the marker up.
let mirror: IVoipClaimPending | null | undefined;

function isVoipClaimPending(value: unknown): value is IVoipClaimPending {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.sessionId === 'string' && typeof candidate.gen === 'number';
}

function readStoredMarker(): IVoipClaimPending | null {
  try {
    const stored: unknown = getStorageItem('sessionStorage', STORAGE_KEY);
    return isVoipClaimPending(stored) ? stored : null;
  } catch (error) {
    logger.warn('[VOIP] AVS-4461 could not read the pending-claim marker', error);
    return null;
  }
}

export function getVoipClaimPending(): IVoipClaimPending | null {
  if (mirror === undefined) {
    mirror = readStoredMarker();
  }
  return mirror;
}

export function setVoipClaimPending(marker: IVoipClaimPending): void {
  mirror = { sessionId: marker.sessionId, gen: marker.gen };
  try {
    setStorageItem('sessionStorage', STORAGE_KEY, mirror);
  } catch (error) {
    logger.warn('[VOIP] AVS-4461 could not persist the pending-claim marker', error);
  }
}

export function clearVoipClaimPending(): void {
  mirror = null;
  try {
    removeStorageItem('sessionStorage', STORAGE_KEY);
  } catch (error) {
    logger.warn('[VOIP] AVS-4461 could not remove the pending-claim marker', error);
  }
}

/** Entering `sessionId`: a marker of any other session no longer applies. */
export function clearVoipClaimPendingOfOtherSession(sessionId: string): void {
  const marker = getVoipClaimPending();
  if (marker && marker.sessionId !== sessionId) {
    clearVoipClaimPending();
  }
}
